import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// eslint-disable-next-line no-restricted-imports
import { Button, Form, Header, Modal, Segment } from 'semantic-ui-react';
// TODO: Lodash should no longer be used here
// eslint-disable-next-line no-restricted-imports
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import MaskedInput from 'react-text-mask';
import Validator from 'validator';

// Import components
import LocationTypePicker from '../../Locations/LocationTypePicker';
import InlineError from '../../Messages/InlineError';
import ShowError from '../../Messages/ShowError';
import StatePicker from '../../Shared/StatePicker';
import HourPicker from './HourPicker';
import LocationInput from './LocationInput';
import TimezonePicker from './TimezonePicker';

// Import utils
import { phoneNumberFormat, phoneNumberParse } from '../../../helpers/utils';

// Import actions
import { RoomCreateMutation, useRoomCreateMutation } from '@wonderschool/common-food-program-data-access';
import {
  locationSelectionCleared,
  organizationAddLocation,
  organizationUpdateLocation,
} from '../../../redux/actions/locationActions';
import { roomAdded } from '../../../redux/actions/roomActions';

interface LocationFormProps {
  onClose?: (status?: string) => void;
}

interface LocationData {
  id: string;
  name: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zipcode: string;
  geometry: {
    lat: string;
    long: string;
  };
  phone: string;
  locationType: string;
  enabled: boolean;
  active: boolean;
  timezone: string;
  capacityGoal: number;
  licenseCapacity: string;
  businessHoursStart: number;
  businessHoursEnd: number;
  primary: boolean;
  ein: string;
  overnightCare: boolean;
}

const LocationForm: FC<LocationFormProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const currentOrganization = useSelector((state: any) => state.organizations.currentOrganization);
  const locations = useSelector((state: any) => state.locations);

  const [data, setData] = useState<LocationData>({
    id: '',
    name: currentOrganization?.name || '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipcode: '',
    geometry: {
      lat: '',
      long: '',
    },
    phone: '',
    locationType: currentOrganization?.programType || '',
    enabled: true,
    active: true,
    timezone: '',
    capacityGoal: 0,
    licenseCapacity: '',
    businessHoursStart: -62167176238000,
    businessHoursEnd: -62167136638000,
    primary: locations?.length === 0 ? true : false,
    ein: '',
    overnightCare: false,
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [disableModalOpen, setDisableModalOpen] = useState(false);

  const [roomCreateMutation] = useRoomCreateMutation();

  useEffect(() => {
    const selectedLocation = locations?.selectedLocation;

    if (!_.isEmpty(selectedLocation?.id) && data.id !== selectedLocation.id) {
      const { phone, ...rest } = selectedLocation;
      setData((prev) => ({
        ...prev,
        phone: phoneNumberParse(phone),
        ...rest,
      }));
    }
  }, [locations?.selectedLocation, data.id]);

  useEffect(() => {
    return () => {
      dispatch(locationSelectionCleared());
    };
  }, [dispatch]);

  const onChange = (e: any, { name, value, type, checked }: any) => {
    if (type === 'checkbox') {
      value = checked;
    }
    setData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => _.omit(prev, name));
  };

  const onLocationSelected = ({ formatted }: any) => {
    const { address1, city, state, zipcode, timezone, geometry } = formatted;

    setData((prev) => ({
      ...prev,
      address1,
      city,
      state,
      zipcode,
      timezone: timezone?.value,
      geometry: {
        lat: geometry.location.lat(),
        long: geometry.location.lng(),
      },
    }));
  };

  const maskedOnChange = (e: any) => {
    setData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setErrors((prev) => _.omit(prev, e.target.name));
  };

  const tryToggleLocationEnabled = (e: any, { name, checked }: any) => {
    if (checked === false) {
      setDisableModalOpen(true);
      return;
    }

    setData((prev) => ({
      ...prev,
      enabled: checked,
    }));
    setErrors((prev) => _.omit(prev, name));
  };

  const forceDisableLocation = () => {
    setData((prev) => ({
      ...prev,
      enabled: false,
    }));
    setErrors((prev) => _.omit(prev, 'enabled'));
    setDisableModalOpen(false);
  };

  const validate = (data: LocationData) => {
    const errors: Record<string, string> = {};
    const locationEnabled = data.enabled;

    if (!data.name) errors.name = t('locations.form.validation.nameRequired');

    if (!locationEnabled) return errors;

    if (!data.address1) errors.address1 = t('locations.form.validation.addressRequired');
    if (!data.city) errors.city = t('locations.form.validation.cityRequired');
    if (!data.state) errors.state = t('locations.form.validation.stateRequired');
    if (!data.zipcode) errors.zipcode = t('locations.form.validation.zipCodeRequired');

    if (!data.phone) {
      errors.phone = t('locations.form.validation.phoneRequired');
    } else {
      if (!Validator.isMobilePhone(data.phone, 'en-US')) errors.phone = t('common.invalidPhone');
    }
    if (!data.locationType) errors.locationType = t('locations.form.validation.locationTypeRequired');
    if (!data.businessHoursStart) errors.businessHoursStart = t('locations.form.validation.openingTimeRequired');
    if (!data.businessHoursEnd) errors.businessHoursEnd = t('locations.form.validation.closingTimeRequired');
    if (!data.timezone) errors.timezone = t('locations.form.validation.timezoneRequired');
    if (!data.capacityGoal) errors.capacityGoal = t('locations.form.validation.capacityGoalRequired');
    if (Number(data.capacityGoal) <= 0) errors.capacityGoal = t('locations.form.validation.capacityGoalPositive');
    if (!data.licenseCapacity) errors.licenseCapacity = t('locations.form.validation.licenseCapacityPositive');
    if (Number(data.licenseCapacity) <= 0)
      errors.licenseCapacity = t('locations.form.validation.licenseCapacityPositive');

    if (!data.overnightCare && data.businessHoursStart && data.businessHoursEnd) {
      if (data.businessHoursStart >= data.businessHoursEnd) {
        errors.businessHoursEnd = t('locations.form.validation.closingTimeAfterOpening');
      }
    }

    return errors;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate(data);
    setErrors(validationErrors);

    if (_.isEmpty(validationErrors)) {
      const { id, phone, ...rest } = data;
      const programType = currentOrganization?.programType;

      setLoading(true);
      const formData = {
        phone: phoneNumberFormat(phone),
        ...rest,
      };

      try {
        if (id) {
          if (currentOrganization?.id) {
            await dispatch(
              organizationUpdateLocation(currentOrganization.id, {
                id,
                ...formData,
              }) as any
            );
            onClose?.('updated');
          }
        } else {
          if (currentOrganization?.id) {
            await dispatch(organizationAddLocation(currentOrganization.id, formData, programType) as any);

            if (programType === 'familyChildCare') {
              const locationId = `${currentOrganization.id}-primaryLocation`;

              roomCreateMutation({
                variables: {
                  input: {
                    locationIdentifier: {
                      firebaseId: locationId,
                    },
                    name: t('locations.form.defaultRoomName'),
                    enabled: true,
                    ageRange: {
                      from: {
                        months: 0,
                        years: 0,
                      },
                      to: {
                        months: 0,
                        years: 0,
                      },
                    },
                  },
                },
                onCompleted: (response: RoomCreateMutation) => {
                  dispatch(
                    roomAdded({
                      organization: currentOrganization.id,
                      location: locationId,
                      name: t('locations.form.defaultRoomName'),
                      id: response.roomCreate.firebaseId || '',
                      ageRange: {
                        from: {
                          months: 0,
                          years: 0,
                        },
                        to: {
                          months: 0,
                          years: 0,
                        },
                      },
                      enabled: true,
                      createdAt: new Date().toISOString(),
                    })
                  );
                  onClose?.('added');
                },
                onError: (error: any) => {
                  console.error('roomCreateMutation error', error);
                  setErrors({ [t('locations.form.errors.unableToAdd')]: error.message });
                },
              });
            }
          }
        }
      } catch (error: any) {
        setLoading(false);
        setErrors({ [t('locations.form.errors.unableToAdd')]: error.message });
      }
    }
  };

  const locationEnabled = data.enabled;
  const programType = currentOrganization?.programType;

  return (
    <Segment basic textAlign="left">
      <ShowError errors={errors} />
      <Form id="location-form" onSubmit={onSubmit} loading={loading} noValidate autoComplete="off">
        <Form.Field error={!!errors.name}>
          <Form.Input
            required
            type="text"
            id="name"
            name="name"
            label={t('common.name')}
            placeholder={t('common.name')}
            value={data.name}
            onChange={onChange}
            autoComplete="off"
          />
          {errors.name && <InlineError text={errors.name} />}
        </Form.Field>

        <Form.Group widths="equal">
          <Form.Field error={!!errors.address1}>
            <LocationInput
              required={locationEnabled}
              id="address1"
              name="address1"
              label={t('locations.form.fields.address1Label')}
              value={data.address1}
              onChange={onChange}
              onLocationSelected={onLocationSelected}
              innerLabel={t('locations.form.fields.address1Placeholder')}
            />
            {errors.address1 && <InlineError text={errors.address1} />}
          </Form.Field>
        </Form.Group>

        <Form.Group widths="equal">
          <Form.Field error={!!errors.address2}>
            <Form.Input
              type="text"
              id="address2"
              name="address2"
              label={t('locations.form.fields.address2Label')}
              placeholder={t('locations.form.fields.address2Placeholder')}
              value={data.address2}
              onChange={onChange}
            />
            {errors.address2 && <InlineError text={errors.address2} />}
          </Form.Field>
        </Form.Group>

        <Form.Group widths="equal">
          <Form.Field error={!!errors.city}>
            <Form.Input
              required={locationEnabled}
              type="text"
              id="city"
              name="city"
              label={t('locations.form.fields.cityLabel')}
              placeholder={t('locations.form.fields.cityLabel')}
              value={data.city}
              onChange={onChange}
            />
            {errors.city && <InlineError text={errors.city} />}
          </Form.Field>

          <Form.Field error={!!errors.state}>
            <Form.Field
              required={locationEnabled}
              id="state"
              name="state"
              label={t('common.state')}
              control={StatePicker}
              placeholder={t('common.state')}
              value={data.state}
              selection
              search
              onChange={onChange}
            />
            {errors.state && <InlineError text={errors.state} />}
          </Form.Field>

          <Form.Field error={!!errors.zipcode}>
            <Form.Input
              required={locationEnabled}
              type="text"
              id="zipcode"
              name="zipcode"
              label={t('locations.form.fields.zipCodeLabel')}
              placeholder={t('locations.form.fields.zipCodeLabel')}
              value={data.zipcode}
              onChange={onChange}
              maxLength="5"
            />
            {errors.zipcode && <InlineError text={errors.zipcode} />}
          </Form.Field>
        </Form.Group>

        <Form.Group widths="equal">
          <Form.Field error={!!errors.phone}>
            <Form.Field
              required={locationEnabled}
              type="text"
              id="phone"
              name="phone"
              label={t('common.phone')}
              placeholder={t('common.phone')}
              control={MaskedInput}
              mask={['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
              value={data.phone}
              guide={false}
              onChange={maskedOnChange}
            />
            {errors.phone && <InlineError text={errors.phone} />}
          </Form.Field>
        </Form.Group>

        {(programType === null || (programType && programType !== 'familyChildCare')) && (
          <Form.Field error={!!errors.locationType}>
            <Form.Field
              translator={t}
              isForm
              required={locationEnabled}
              id="locationType"
              name="locationType"
              label={t('locations.form.fields.locationTypeLabel')}
              control={LocationTypePicker}
              placeholder={t('locations.form.fields.locationTypePlaceholder')}
              value={data.locationType}
              selection
              search
              onChange={onChange}
            />
            {errors.locationType && <InlineError text={errors.locationType} />}
          </Form.Field>
        )}

        <Header as="h3" dividing>
          {t('locations.form.sections.licenseInformation')}
        </Header>

        <Form.Group widths="equal">
          <Form.Field error={!!errors.licenseCapacity}>
            <Form.Input
              required={locationEnabled}
              type="number"
              min={0}
              id="licenseCapacity"
              name="licenseCapacity"
              label={t('locations.form.fields.licenseCapacityLabel')}
              placeholder={t('locations.form.fields.licenseCapacityPlaceholder')}
              value={data.licenseCapacity}
              onChange={onChange}
            />
            {errors.licenseCapacity && <InlineError text={errors.licenseCapacity} />}
          </Form.Field>

          <Form.Field error={!!errors.capacityGoal}>
            <Form.Input
              required={locationEnabled}
              type="number"
              min={0}
              id="capacityGoal"
              name="capacityGoal"
              label={t('locations.form.fields.capacityGoalLabel')}
              placeholder={t('locations.form.fields.capacityGoalPlaceholder')}
              value={data.capacityGoal}
              onChange={onChange}
            />
            {errors.capacityGoal && <InlineError text={errors.capacityGoal} />}
          </Form.Field>
        </Form.Group>

        <Header as="h3" dividing>
          {t('locations.form.sections.taxInformation')}
        </Header>

        <Form.Group widths="equal">
          <Form.Field error={!!errors.ein}>
            <Form.Input
              type="number"
              min={0}
              id="ein"
              name="ein"
              label={t('locations.form.fields.einLabel')}
              placeholder={t('locations.form.fields.einLabel')}
              value={data.ein}
              onChange={onChange}
            />
            {errors.ein && <InlineError text={errors.ein} />}
          </Form.Field>
        </Form.Group>

        <Header as="h3" dividing>
          {t('locations.form.sections.businessHours')}
        </Header>

        <Form.Group widths="equal">
          <Form.Field>
            <Form.Checkbox
              checked={data.overnightCare}
              id="overnightCare"
              name="overnightCare"
              label={t('locations.form.fields.overnightCareLabel')}
              onChange={onChange}
              toggle
            />
          </Form.Field>
        </Form.Group>

        <Form.Group widths="equal">
          <Form.Field error={!!errors.businessHoursStart}>
            <Form.Field
              required={locationEnabled}
              id="businessHoursStart"
              name="businessHoursStart"
              label={t('locations.form.fields.openingTimeLabel')}
              control={HourPicker}
              placeholder={t('locations.form.fields.openingTimePlaceholder')}
              value={data.businessHoursStart}
              selection
              search
              onChange={onChange}
            />
            {errors.businessHoursStart && <InlineError text={errors.businessHoursStart} />}
          </Form.Field>

          <Form.Field error={!!errors.businessHoursEnd}>
            <Form.Field
              required={locationEnabled}
              id="businessHoursEnd"
              name="businessHoursEnd"
              label={t('locations.form.fields.closingTimeLabel')}
              control={HourPicker}
              placeholder={t('locations.form.fields.closingTimePlaceholder')}
              value={data.businessHoursEnd}
              selection
              search
              onChange={onChange}
            />
            {errors.businessHoursEnd && <InlineError text={errors.businessHoursEnd} />}
          </Form.Field>
        </Form.Group>

        <Form.Field error={!!errors.timezone}>
          <Form.Field
            translator={t}
            isForm
            required={locationEnabled}
            id="timezone"
            name="timezone"
            label={t('locations.form.fields.timezoneLabel')}
            control={TimezonePicker}
            placeholder={t('locations.form.fields.timezonePlaceholder')}
            value={data.timezone}
            selection
            search
            onChange={onChange}
          />
          {errors.timezone && <InlineError text={errors.timezone} />}
        </Form.Field>

        {/* {false && (
          <Form.Field error={!!errors.enabled}>
            <label>
              {t('Location enabled?')}
              <Popup
                content={t(
                  "Locations are enabled as default. This is a flag for you to keep track of your locations. Disabling it won't impact anything."
                )}
                position="right center"
                offset={[4, 0]}
                trigger={<Icon name="info circle" />}
              />
            </label>
            <Form.Radio
              toggle
              id="enabled"
              name="enabled"
              onChange={tryToggleLocationEnabled}
              control={Checkbox}
              checked={data.enabled}
            />
            {errors.enabled && <InlineError text={errors.enabled} />}
          </Form.Field>
        )} */}

        <Modal open={disableModalOpen}>
          <Modal.Header>{t('locations.form.disableDialog.title')}</Modal.Header>
          <Modal.Content>
            <Modal.Description>{t('locations.form.disableDialog.message')}</Modal.Description>
          </Modal.Content>
          <Modal.Actions>
            <Button color="red" onClick={() => setDisableModalOpen(false)}>
              {t('common.no')}
            </Button>
            <Button
              content={t('common.yes')}
              labelPosition="right"
              icon="checkmark"
              onClick={forceDisableLocation}
              positive
            />
          </Modal.Actions>
        </Modal>

        <br />

        <Form.Group>
          <Form.Button primary content={data.id ? t('common.update') : t('locations.form.saveAction')} />
          <Form.Button
            basic
            content={t('common.cancel')}
            onClick={(e) => {
              if (e) e.preventDefault();
              if (onClose) onClose();
            }}
          />
        </Form.Group>
      </Form>
    </Segment>
  );
};

export default LocationForm;
